/*
 * =============================================================================
 * FILE HEADER — packages/ir/src/ulid.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why             | What Changed           |
 * |---|------------------------|-------------------|-----------------|------------------------|
 * | 1 | 2026-06-16 (Phase 1)   | Claude Sonnet 4.6 | IR Step 1       | generateId using ULID  |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - packages/ir/src/index.ts (re-exported as generateId)
 * - packages/sdk/src/index.ts (generates step IDs)
 * - apps/studio/src/device/recorder.ts (generates step IDs)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * generateId()  — returns a new ULID string (26 chars, Crockford Base32)
 * isValidId()   — verifies a string looks like a ULID (26 chars, valid alphabet)
 * ULID_ALPHABET — exported constant for test / validation use
 *
 * CALLER → CALLEE MAP
 * --------------------
 * generateId()  → ulid() (from 'ulid' npm package)
 * isValidId()   → regex check against ULID alphabet
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. ULIDs are monotonically sortable by creation time (first 10 chars = timestamp).
 *    This makes database queries by creation order work without a separate timestamp.
 * 2. The `ulid` package is a CJS package; importing it as ESM requires the `ulid`
 *    named export (not default). Works in both Node and browser (browser needs no
 *    crypto.getRandomValues polyfill in modern runtimes).
 * 3. Phase 0 used crypto.randomUUID() for IDs — those are UUIDs (36 chars with
 *    dashes). Phase 1 migrated to ULIDs (26 chars, no dashes). Old UUID ids still
 *    pass IRStep.id (z.string().min(1)); the schema intentionally does not enforce
 *    ULID format to stay forward/backward compatible.
 *
 * ISSUES FACED
 * ------------
 * None
 *
 * =============================================================================
 */
import { ulid } from "ulid";
/** Valid characters in Crockford Base32 (the alphabet ULID uses). */
export const ULID_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;
/** Generate a new time-sortable unique ID. */
export function generateId() {
    return ulid();
}
/** Return true if s looks like a valid ULID (26 Crockford-Base32 chars). */
export function isValidId(s) {
    return ULID_RE.test(s);
}
//# sourceMappingURL=ulid.js.map